/* === Seitentitel in die Filterleiste einpassen (Shop + Suchergebnisse) ===
   Der Titel liegt auf dem Desktop absolut mittig ueber der Filterleiste (siehe
   component-filterbar-title.css). Zwischen den Filtern links und der Sortierung rechts
   bleibt nur eine begrenzte Luecke — und wie breit die ist, laesst sich im CSS nicht
   ausrechnen: "Filter:", "Sortieren nach:" und die Produktzahl haben FESTE Schriftgroessen
   und skalieren daher nicht mit dem Raster (--cell) mit. Eine feste Breite im CSS trifft
   den echten Platz deshalb immer nur bei einer einzigen Fensterbreite.

   Also wird hier gemessen: erst die Luecke, dann der Titel, dann wird er hineingezogen.

   Eskalation wie bei pdp-zone-fit.js, aus demselben Grund: Laufweite zuerst, Schriftgroesse
   zuletzt. Enger gesetzte Zeichen fallen beim Lesen kaum auf, eine kleinere Schrift schon.

   Angriffspunkte sind zwei CSS-Variablen am Titel, deren Wirkung im CSS definiert ist:
   --title-ls (Laufweite) und --title-fit (Schriftgroesse in px). Faellt dieses Skript aus,
   greift dort der clamp-Fallback. */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 750px)');

  const PAD = 14;           // Mindestabstand zu Filter und Sortierung, in px
  const MAX_TIGHTEN = 0.03; // Laufweite hoechstens so weit zusammenziehen, in em
  const MIN_FS = 11;        // Untergrenze der Schriftgroesse, in px
  const ROUNDS = 4;         // Nachmessungen beim Verkleinern

  // Zwei Seiten benutzen den Balken: der Shop (.collection-with-sidebar__main, Titel =
  // Kategoriename) und die Suchergebnisse (.search-layout, Titel = "Produkte"). Auf einer
  // Seite gibt es immer nur einen davon, deshalb genuegt eine gemeinsame Auswahl.
  // Gestaltung: assets/component-filterbar-title.css.
  const SCOPE = '.collection-with-sidebar__main, .search-layout';

  const getTitle = () =>
    document.querySelector(
      '.collection-with-sidebar__main > .collection-page-title, .search-layout > .collection-page-title'
    );
  const getBar = () =>
    document.querySelector(
      '.collection-with-sidebar__main .facets-container, .search-layout .facets-container'
    );

  // Die sichtbaren INHALTE der Leiste — alles ohne sichtbare Element-Kinder.
  // Container wuerden die volle Breite ueberspannen und die Luecke auf null druecken;
  // ein <select> zaehlt trotz seiner <option>-Kinder als Inhalt, weil die keine
  // Client-Rects haben. So bleibt die Messung unabhaengig von der Markup-Struktur,
  // die Shopify fuer die Facetten liefert.
  const contentRects = (root) => {
    const out = [];
    for (const el of root.querySelectorAll('*')) {
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      const hasVisibleChild = Array.from(el.children).some((c) => c.getClientRects().length > 0);
      if (hasVisibleChild) continue;
      out.push(r);
    }
    return out;
  };

  let scheduled = false;

  const fit = () => {
    scheduled = false;
    const title = getTitle();
    if (!title) return;

    // Zuruecksetzen und vom vollen Entwurfswert aus neu einpassen. Ohne das wuerde jede
    // Anpassung nur ihr eigenes Ergebnis nachmessen: der Titel schrumpfte bei jedem
    // Resize weiter und kaeme nie wieder hoch.
    title.style.removeProperty('--title-fit');
    title.style.removeProperty('--title-ls');

    const bar = getBar();
    if (!DESKTOP.matches || !bar) return;

    const barRect = bar.getBoundingClientRect();
    const titleRect = title.getBoundingClientRect();
    const centre = titleRect.left + titleRect.width / 2;

    // Luecke um die Titelmitte: naechste Kante links, naechste Kante rechts.
    let left = barRect.left;
    let right = barRect.right;
    for (const r of contentRects(bar)) {
      if (r.right <= centre) left = Math.max(left, r.right);
      else if (r.left >= centre) right = Math.min(right, r.left);
    }

    // Der Titel ist um seine EIGENE Mitte zentriert (left: 50% + translateX(-50% …)) und
    // waechst deshalb symmetrisch nach beiden Seiten. Diese Mitte liegt wegen des Versatzes
    // (--title-x) aber nicht mittig in der Luecke. Nutzbar ist daher nicht die ganze
    // Luecke, sondern nur das Doppelte der KLEINEREN Haelfte — sonst stoesst der Titel auf
    // der engeren Seite an die Filter, waehrend auf der anderen Platz frei bleibt.
    const avail = 2 * (Math.min(centre - left, right - centre) - PAD);
    if (avail <= 0) return;

    // Startpunkt ist die voll ausgeschriebene Groesse aus dem CSS. Kurze Namen bleiben
    // dadurch exakt so gross wie im Entwurf, statt auf den clamp-Schaetzwert zu fallen.
    const maxFs = parseFloat(getComputedStyle(title).getPropertyValue('--title-fs-max')) || 26;
    title.style.setProperty('--title-fit', maxFs + 'px');

    // Aussenbreite messen, nicht scrollWidth: der Titel traegt padding (0 1.5rem), und
    // genau diese Gesamtbreite muss in die Luecke passen.
    const widthOf = () => title.getBoundingClientRect().width;

    if (widthOf() <= avail) return;

    // 1. Laufweite zusammenziehen.
    title.style.setProperty('--title-ls', -MAX_TIGHTEN + 'em');

    // 2. Schriftgroesse verkleinern, bis es passt. Der erste Schritt trifft es fast
    //    (Breite skaliert nahezu linear mit der Groesse), die Nachmessungen fangen
    //    Rundung und Kerning ab.
    for (let i = 0; i < ROUNDS; i++) {
      const width = widthOf();
      if (width <= avail) break;
      const current = parseFloat(getComputedStyle(title).fontSize);
      const next = Math.max(MIN_FS, current * (avail / width));
      if (next >= current - 0.2) break; // keine Verbesserung mehr moeglich
      title.style.setProperty('--title-fit', next + 'px');
    }
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(fit);
  };

  window.addEventListener('resize', schedule);
  DESKTOP.addEventListener('change', schedule);
  // Facetten tauschen die Leiste per AJAX aus (facets.js) — danach neu einpassen.
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('pageshow', schedule);

  const target = document.querySelector(SCOPE);
  if (target) new MutationObserver(schedule).observe(target, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  // Webfonts aendern die Textbreite, sobald sie geladen sind.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
})();
